import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/** URL parameter that switches the run activity log (log icon, raw model call checkbox) on or off. */
export const ACTIVITY_LOG_PARAM = 'activitylog';
/** sessionStorage key: the choice lasts for this browser tab's session, and is not shared with other tabs. */
export const ACTIVITY_LOG_SESSION_KEY = 'activity_log_enabled';
// Used only when sessionStorage is unavailable (some private modes): the choice then lasts as long as the page.
let memoryValue: boolean | null = null;

function readStored(): boolean {
  try {
    return window.sessionStorage.getItem(ACTIVITY_LOG_SESSION_KEY) === '1';
  } catch {
    return memoryValue ?? false; // storage unavailable: fall back to what this page last chose
  }
}

function store(on: boolean) {
  memoryValue = on;
  try { window.sessionStorage.setItem(ACTIVITY_LOG_SESSION_KEY, on ? '1' : '0'); } catch { /* storage unavailable */ }
}

function readParam(search: string): boolean | null {
  const v = new URLSearchParams(search).get(ACTIVITY_LOG_PARAM);
  return v === '1' ? true : v === '0' ? false : null;
}

/**
 * Whether the run activity log is shown on the control view page. Off by default.
 *
 *   ?activitylog=1  turns it on for this tab's session
 *   ?activitylog=0  turns it off
 *   any other value is ignored
 *
 * The parameter is removed from the address bar once it has been read; the choice is kept in sessionStorage.
 * This only controls what the page shows. It is not an access control.
 */
export function useActivityLogFeature(): { enabled: boolean } {
  const location = useLocation();
  const navigate = useNavigate();
  // A parameter in the URL wins from the very first render, so there is no flash of the wrong state.
  const [enabled, setEnabled] = useState<boolean>(() => readParam(location.search) ?? readStored());

  useEffect(() => {
    const requested = readParam(location.search);
    if (requested === null) return;
    store(requested);
    setEnabled(requested);

    const params = new URLSearchParams(location.search);
    params.delete(ACTIVITY_LOG_PARAM);
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true, state: location.state },
    );
  }, [location.search, location.pathname, location.hash, location.state, navigate]);

  return { enabled };
}
